import React, { useEffect, useState } from 'react';
import { api } from '../api';

const ROLES = [
  { id: 'owner', label: 'Owner', tone: 'bg-indigo-500/20 text-indigo-300 border-indigo-400/40' },
  { id: 'message', label: 'Best to message', tone: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40' },
  { id: 'technical', label: 'Technical', tone: 'bg-cyan-500/20 text-cyan-300 border-cyan-400/40' },
  { id: 'exec', label: 'Exec sponsor', tone: 'bg-amber-500/20 text-amber-300 border-amber-400/40' },
  { id: 'support', label: 'Support', tone: 'bg-slate-500/20 text-slate-300 border-slate-400/40' },
];

/**
 * Multi-user chip picker for a single company. Use on the Company Detail panel.
 *
 * Props:
 *   companyId — required
 *   onChange  — called with the new assignment list after each change
 */
export default function CompanyAssignments({ companyId, onChange }) {
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [pickerUserId, setPickerUserId] = useState('');
  const [pickerRole, setPickerRole] = useState('owner');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  async function load() {
    if (!companyId) return;
    const [a, u] = await Promise.all([
      api.listCompanyAssignments(companyId),
      api.listUsers().catch(() => []),
    ]);
    setAssignments(a || []);
    setUsers((u || []).filter((x) => x.active));
    onChange?.(a || []);
  }

  async function addAssignment() {
    if (!pickerUserId) return;
    await api.addCompanyAssignment(companyId, { user_id: pickerUserId, role: pickerRole });
    setPickerUserId('');
    setPickerRole('owner');
    setAdding(false);
    load();
  }

  async function removeAssignment(userId) {
    await api.removeCompanyAssignment(companyId, userId);
    load();
  }

  async function changeRole(userId, newRole) {
    await api.updateCompanyAssignment(companyId, userId, { role: newRole });
    load();
  }

  const availableUsers = users.filter(
    (u) => !assignments.some((a) => a.user_id === u.id)
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Assigned</div>
      <div className="flex flex-wrap gap-2">
        {assignments.length === 0 && (
          <span className="text-slate-500 text-sm italic">No one assigned yet.</span>
        )}
        {assignments.map((a) => {
          const role = ROLES.find((r) => r.id === a.role) || ROLES[0];
          return (
            <div
              key={a.user_id}
              className={`flex items-center gap-2 px-3 py-1.5 border rounded-full text-xs ${role.tone}`}
            >
              <span className="font-medium">{a.user_name}</span>
              <select
                value={a.role}
                onChange={(e) => changeRole(a.user_id, e.target.value)}
                className="bg-transparent border-none outline-none text-xs cursor-pointer"
                title="Change role"
              >
                {ROLES.map((r) => (
                  <option key={r.id} value={r.id} className="bg-slate-800">
                    {r.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => removeAssignment(a.user_id)}
                className="text-slate-400 hover:text-white"
                title="Remove"
              >
                ×
              </button>
            </div>
          );
        })}

        {!adding && availableUsers.length > 0 && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1 px-3 py-1.5 border border-dashed border-slate-600 rounded-full text-xs text-slate-400 hover:text-white hover:border-slate-400"
          >
            + Assign someone
          </button>
        )}
      </div>

      {adding && (
        <div className="flex items-center gap-2 p-2 bg-slate-800 border border-slate-700 rounded-lg">
          <select
            value={pickerUserId}
            onChange={(e) => setPickerUserId(e.target.value)}
            className="flex-1 bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm"
          >
            <option value="">Pick a teammate…</option>
            {availableUsers.map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
            ))}
          </select>
          <select
            value={pickerRole}
            onChange={(e) => setPickerRole(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm"
          >
            {ROLES.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
          <button onClick={addAssignment} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded text-sm">
            Add
          </button>
          <button onClick={() => { setAdding(false); setPickerUserId(''); }} className="px-3 py-1.5 text-sm text-slate-400 hover:text-white">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
